import {
    Box,
    Flex,
    Heading,
    IconButton,
    Image,
    Link,
    Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaIcon, ServiceIcon, SkeletonMotion } from '@acp/core-ui';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { useMemo } from 'react';
import { format, max } from 'date-fns';
import { brandIcons } from '@acp/core-ui/assets';
import {
    PortfolioService, PortfolioServiceTag, ServiceIdMapper, ServiceShortName,
} from '@acp/shared';
import { ReportedFindingsMachineContext } from '../../machine/context';
import { useBasicProperty, useProperty } from '../../../../../property';

function ComplianceBadgeIcon({ tags }: { tags: string[] }) {
    if (tags.includes(PortfolioServiceTag.ComplianceIssues)) {
        return <Image src={brandIcons.complianceIssue} h="30px" />;
    }

    if (
        tags.includes(PortfolioServiceTag.PendingItems)
        || tags.includes(PortfolioServiceTag.LegacyPendingItems)
    ) {
        return <Image src={brandIcons.pendingItems} h="30px" />;
    }

    return null;
}

function LastStandardServiceDate({ service }: { service: PortfolioService[] }) {
    if (service.length === 0) return null;

    const selectedService = service[0];
    const dates = [
        selectedService.lastStandardBookingDate,
        selectedService.lastFullServiceBookingDate,
    ].filter((date) => date !== null) as string[];

    if (dates.length === 0) {
        return (
            <Text fontSize="sm" pt={1} color="muted.500">
                No service completed yet
            </Text>
        );
    }

    const lastServiceDate = format(max(dates), 'd MMMM yyyy');

    return (
        <Flex align="center" color="muted.500" pt={1} gap={2}>
            <Text fontSize="sm" fontWeight="medium">
                Service performed on
                {' '}
                {lastServiceDate}
                :
            </Text>
            <ServiceIcon
                key={selectedService.serviceId}
                service={selectedService.shortName}
                height="20"
            />
        </Flex>
    );
}

function ReportedFindingsWizardHeader({
    compliancePropertyId,
}: {
    compliancePropertyId: number;
}) {
    const machine = ReportedFindingsMachineContext.useActorRef();

    const serviceShortName = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.serviceShortName
    );

    const rawFindingResponse = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.apiResponse
    );

    const isMinimumStandards = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.serviceShortName === ServiceShortName.MinimumStandards
    );

    const documentServiceCode = serviceShortName === null
        ? undefined
        : ServiceIdMapper.getDocumentServiceCode(serviceShortName);

    const basicProperty = useBasicProperty(compliancePropertyId, {});
    const property = useProperty(compliancePropertyId, {});
    const selectedService = useMemo(() => property?.data?.services.filter(
        (service) => service.shortName === serviceShortName
    ) ?? [], [property.data]);

    return (
        <Flex direction="row" gap={4}>
            <Box pt={1}>
                <ComplianceBadgeIcon tags={rawFindingResponse?.tags ?? []} />
            </Box>
            <Flex
                direction="column"
                flex="1"
                gap={1}
            >
                <Flex
                    direction={{ base: 'column', xl: 'row' }}
                    align={{ base: 'flex-start', xl: 'center' }}
                    w="full"
                >
                    <Flex direction="column" flex="1" gap={2}>
                        {
                            basicProperty.status === 'success'
                                ? <Heading size="md">{basicProperty.data.address.full}</Heading>
                                : (
                                    <SkeletonMotion width="50%">
                                        <Heading size="md">Loading...</Heading>
                                    </SkeletonMotion>
                                )
                        }
                        {
                            property.status === 'success'
                                && <LastStandardServiceDate service={selectedService} />
                        }
                    </Flex>
                    {
                        property.status === 'success'
                        && !isMinimumStandards
                        && documentServiceCode !== undefined
                        && (
                            <Link
                                as={RouterLink}
                                to={`/property/${compliancePropertyId}/documents?filter=SERVICE_REPORT&service=${documentServiceCode}`}
                                fontWeight="normal"
                                fontSize="md"
                                target="_blank"
                                ml={{ base: 0, xl: 'auto' }}
                                mt={{ base: 2, xl: 0 }}
                            >
                                View service report
                            </Link>
                        )
                    }
                </Flex>
            </Flex>
            <Box
                ml="auto"
                mt={{ base: 2, xl: 0 }}
                alignSelf={{ base: 'flex-start', xl: 'center' }}
            >
                <IconButton
                    variant="link"
                    icon={<FaIcon icon={faTimes} />}
                    aria-label="Close"
                    onClick={() => machine.send({ type: 'CLOSE_MODAL' })}
                    color="black"
                />
            </Box>
        </Flex>
    );
}

export { ReportedFindingsWizardHeader };
